import type { ApiResponse as SubscriptionsResponse } from '@web/query/App/Stripe/useSubscriptions';

export function hasSubscription(subscription: any): subscription is { hasSubscription: true } {
  return (subscription?.hasSubscription && subscription?.hasSubscriptionMetadata) ?? false;
}

export function isIndividualPlan(productId: string | undefined, individualProductId: string | undefined): boolean {
  return !!productId && !!individualProductId && productId === individualProductId;
}

export type ConversionStatus = 'trialing' | 'paused';

/**
 * The two Stripe statuses under which an organisation that already holds a subscription may sit on
 * plan selection: mid-trial with no card, and paused after the trial ended. For either, plan selection
 * is the way to a card and a paid plan. Every other status with a subscription is sent away exactly
 * as before, and no subscription at all is not a conversion.
 */
export function getConversionStatus(data: SubscriptionsResponse | undefined): ConversionStatus | null {
  if (!hasSubscription(data)) return null;

  const { status, hasPaymentMethod } = (data as Extract<SubscriptionsResponse, { hasSubscription: true }>)
    .subscription;

  /**
   * A Checkout trial carries a card, and converts through the Active screen's own activate-now path.
   * Only a trial with nothing Stripe could charge belongs in the picker's conversion mode; sending a
   * card-bearing trial there would offer it a second card instead of an in-place plan change.
   */
  if (status === 'trialing') return hasPaymentMethod ? null : 'trialing';

  /**
   * Stripe reaches status 'paused' only via trial_settings.end_behavior.missing_payment_method, so a
   * paused subscription is by definition a trial that ended with no card. A payment-failure lock is
   * past_due or unpaid in Stripe and is deliberately excluded — that organisation needs the customer
   * portal on the lock screen, not a plan picker.
   */
  return status === 'paused' ? 'paused' : null;
}
